<template>
    <l-map
        id="leafletMap"
        ref="mapRef"
        v-model="zoom"
        data-test-id="leaflet-map"
        :maxBounds="maxBounds"
        :maxBoundsViscosity="0.6"
        :center="[0, 0]"
        :options="{ zoomControl: false }"
        @click="handleMapClick"
        @ready="mapReady()"
        @moveend="mapMoved"
    >
        <l-tile-layer
            :url="currentMapType.url"
            :attribution="currentMapType.attribution"
            :minZoom="currentMapType.minZoom"
            :maxZoom="currentMapType.maxZoom"
            :options="{
                bounds: [
                    [-90, -180],
                    [90, 180],
                ],
            }"
            :noWrap="true"
        />
        />

        <slot></slot>

        <l-control
            v-if="$slots.topLeft"
            position="topleft"
        >
            <div
                ref="topLeftControls"
                class="top-left-controls"
            >
                <slot name="topLeft"></slot>
            </div>
        </l-control>

        <l-control-zoom position="topright" />

        <!-- Distance scale -->
        <l-control-scale position="bottomleft" />

        <!-- Full Screen toggle -->
        <l-control position="bottomleft">
            <div class="leaflet-bar">
                <fullscreen-toggle
                    :mapObj="mapObjectRef"
                    :mapContainer="mapContainer"
                />
            </div>
        </l-control>

        <!-- Center map on user locations -->
        <l-control position="bottomleft">
            <div class="leaflet-bar">
                <a
                    class="map-button map-button--center"
                    data-test-id="map-recenter-button"
                    href="#"
                    title="Center"
                    role="button"
                    aria-label="Center"
                    @click.prevent="recenter"
                />
            </div>
        </l-control>

        <!-- Map types -->
        <l-control position="bottomleft">
            <div class="leaflet-bar">
                <div
                    v-click-outside="() => (mapTypesVisible = false)"
                    :class="{ active: mapTypesVisible }"
                    data-test-id="map-type-menu-toggle"
                    class="map-type-menu"
                >
                    <ul>
                        <li
                            v-for="(mapType, index) in mapTypes"
                            :key="index"
                            :data-test-id="`map-type-option-${mapType.name}`"
                            :class="{ active: index === currentMapTypeIndex }"
                            @click="currentMapTypeIndex = index"
                        >
                            <span :data-test-id="`map-type-option-${mapType.name}-text`">{{ mapType.name }}</span>
                        </li>
                    </ul>
                </div>
                <a
                    class="map-button map-button--layers"
                    href="#"
                    title="Layers"
                    role="button"
                    aria-label="Layers"
                    @click.stop.prevent="mapTypesVisible = true"
                />
            </div>
        </l-control>
    </l-map>
</template>

<script>
import L from 'leaflet'
import { LMap, LTileLayer, LControl, LControlScale, LControlZoom } from '@vue-leaflet/vue-leaflet'
import FullscreenToggle from '@/components/Map/FullscreenToggle.vue'
import * as utils from '@/components/Map/utils/index.js'

export default {
    components: {
        LMap,
        LTileLayer,
        FullscreenToggle,
        LControl,
        LControlScale,
        LControlZoom,
    },

    props: {
        mapContainer: {
            type: undefined,
            required: true,
        },
        settings: {
            type: undefined,
            required: false,
            default: {},
        },
    },

    emits: ['map-clicked'],

    data() {
        const mapboxToken =
            'pk.eyJ1Ijoiam9zaG1ha2luZGEiLCJhIjoiY2ltYzVia2d6MDRid3U1a2ltZnBmemxnbCJ9.-4nVyK_cgBWxXEV1TNg9VQ'

        return {
            maxBounds: L.latLngBounds([-90, -240], [90, 240]),
            mounted: false,
            clusterOptions: {
                maxClusterRadius: 30,
                zoomToBoundsOnClick: false,
                spiderfyOnMaxZoom: false,
                iconCreateFunction: (cluster) =>
                    L.divIcon({
                        html: `<div>${cluster.getChildCount()}</div>`,
                        className: 'pli-cluster-custom',
                        iconSize: new L.Point(40, 40),
                    }),
                spiderfyDistanceMultiplier: 1.5,
            },
            mapObjectRef: null,
            currentMapTypeIndex: 1,
            mapTypesVisible: false,
            mapTypes: [
                {
                    name: 'Streets',
                    minZoom: 3,
                    maxZoom: 18,
                    url:
                        'https://api.mapbox.com/styles/v1/mapbox/streets-v9/tiles/256/{z}/{x}/{y}' +
                        `?access_token=${mapboxToken}`,
                    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                },
                {
                    name: 'Topographical',
                    minZoom: 3,
                    maxZoom: 18,
                    url:
                        'https://api.mapbox.com/styles/v1/mapbox/outdoors-v9/tiles/256/{z}/{x}/{y}' +
                        `?access_token=${mapboxToken}`,
                    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                },
                {
                    name: 'Satellite',
                    minZoom: 3,
                    maxZoom: 18,
                    url:
                        'https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/256/{z}/{x}/{y}' +
                        `?access_token=${mapboxToken}`,
                    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                },
                {
                    name: 'Satellite + Streets',
                    minZoom: 3,
                    maxZoom: 18,
                    url:
                        'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v9/tiles/256/{z}/{x}/{y}' +
                        `?access_token=${mapboxToken}`,
                    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                },
            ],
            zoom: 5,
            viewportCenter: this.getViewportCenter(),
        }
    },

    computed: {
        currentMapType() {
            return this.mapTypes[this.currentMapTypeIndex]
        },
    },

    unmounted() {
        this.$eventBus.off('center-on')
    },

    methods: {
        mapReady() {
            this.mapObjectRef = this.$refs.mapRef.leafletObject

            this.$eventBus.on('center-on', (data) => {
                this.centerOn(data)
            })

            this.$eventBus.on('center-if-covered', (coords) => {
                if (this.coordinatesAreUnderControls(coords)) {
                    this.mapObjectRef.panTo(L.latLng(coords.latitude, coords.longitude))
                }
            })

            this.createPosition('bottomleft-inner', 'leaflet-bottom leaflet-left-inner')

            if (this.settings.clusterOverlappingPlis) {
                this.setClusterClick()
            }

            // sync leaflet with vue-leaflet
            this.mapObjectRef.setView(L.latLng(0, 0), this.zoom)
            window.$leafletMap = this.mapObjectRef
            this.mounted = true
        },

        createPosition(label, className) {
            // Add bottomleft-inner position for controls
            const container = this.mapObjectRef._controlContainer
            const corners = this.mapObjectRef._controlCorners

            const div = document.createElement('div')
            div.className = className
            corners[label] = div
            container.appendChild(div)
        },

        centerOn(coords) {
            const objectsToCenterOn = Array.isArray(coords) ? coords : [coords]
            const bounds = []
            objectsToCenterOn.forEach((object) => {
                if (object.points) {
                    bounds.push(utils.getAllLatLongsForShape(object))
                } else {
                    bounds.push(utils.getLatLong(object))
                }
            })
            this.mapObjectRef.flyToBounds(bounds, { duration: 0.75 })
        },

        coordinatesAreUnderControls({ latitude, longitude }) {
            if (!this.$refs.topLeftControls) {
                return false
            }

            const point = this.mapObjectRef.latLngToContainerPoint([latitude, longitude])
            const mapRect = this.mapObjectRef.getContainer().getBoundingClientRect()
            const paneRect = this.$refs.topLeftControls.getBoundingClientRect()

            const paneRelative = {
                top: paneRect.top - mapRect.top,
                bottom: paneRect.bottom - mapRect.top,
                left: paneRect.left - mapRect.left,
                right: paneRect.right - mapRect.left,
            }

            // Check if point is inside of pane bounds
            const isInsideX = point.x >= paneRelative.left && point.x <= paneRelative.right
            const isInsideY = point.y >= paneRelative.top && point.y <= paneRelative.bottom

            return isInsideX && isInsideY
        },

        recenter() {
            const bounds = L.latLngBounds([])

            this.mapObjectRef.eachLayer((layer) => {
                if (layer instanceof L.FeatureGroup) {
                    bounds.extend(layer.getBounds())
                }
            })

            if (bounds.isValid()) {
                this.mapObjectRef.fitBounds(bounds, { duration: 0.75 })
            }
        },

        setClusterClick() {
            this.$nextTick(() => {
                this.mapObjectRef.on('clusterclick', (a) => {
                    a.layer.spiderfy()
                })
            })
        },

        getViewportCenter() {
            if (this.mapObjectRef) {
                const leafletMap = this.mapObjectRef
                const mapCenterLatLng = leafletMap.getCenter()
                const mapCenterPixels = leafletMap.latLngToLayerPoint(mapCenterLatLng)
                mapCenterPixels.x -= 300 / 2 // Adjust to account for right panel overlay
                return leafletMap.layerPointToLatLng(mapCenterPixels)
            }
            return L.latLng(0, 0)
        },

        mapMoved() {
            this.viewportCenter = this.getViewportCenter()
        },

        handleMapClick() {
            if (!this.mapTypesVisible) {
                this.$emit('map-clicked')
            }
        },
    },
}
</script>

<style scoped>
@import '../../../../node_modules/vue-leaflet-markercluster/dist/style.css';
#leafletMap {
    position: relative;
    border-radius: 0 0 8px 8px;
    font-family: 'Avenir Book';
    color: #fff;
}

.top-left-controls {
    pointer-events: none;
}

.leaflet-control:has(.top-left-controls) {
    pointer-events: none;
}
</style>
